import React, { useEffect, useState, useRef } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SettingsIcon from '@mui/icons-material/Settings';
import MainModal from '../UI/Modal/MainModal';
import useSchoolNotifications from '../../hooks/useSchoolNotifications';
import css from './SchoolNotificationCenter.module.css';

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function pushCanBeRequested() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && Notification.permission !== 'denied';
}

export default function SchoolNotificationCenter({ inline = false }) {
  const [open, setOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const containerRef = useRef(null);
  const {
    items,
    unreadCount,
    loading,
    pushStatus,
    pushBusy,
    refresh,
    markOneRead,
    markAllRead,
    enablePush,
    disablePush,
    preferences,
    updatePreferences,
    actor,
    dashboardPath,
  } = useSchoolNotifications();
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushPromptBusy, setPushPromptBusy] = useState(false);
  const [pushPromptError, setPushPromptError] = useState('');

  const categories = [
    ['exams', 'Exams and results'],
    ['attendance', 'Attendance'],
    ['fees', 'Fees and payments'],
    ['calendar', 'Calendar and timetable'],
    ['messages', 'Messages'],
    ['workflows', 'Workflows'],
    ['digest', 'Weekly digest'],
  ];

  useEffect(() => {
    if (!actor || pushStatus === 'unknown' || pushStatus === 'enabled' || !pushCanBeRequested()) return;
    const promptKey = `school-push-prompt-dismissed:${actor}`;
    if (localStorage.getItem(promptKey) !== 'true') setShowPushPrompt(true);
  }, [actor, pushStatus]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open]);

  const dismissPushPrompt = () => {
    if (actor) localStorage.setItem(`school-push-prompt-dismissed:${actor}`, 'true');
    setShowPushPrompt(false);
  };

  const handlePromptEnable = async () => {
    setPushPromptBusy(true);
    setPushPromptError('');
    const enabled = await enablePush();
    if (enabled) dismissPushPrompt();
    else setPushPromptError('Push notifications could not be enabled here. Check your browser permissions and try again.');
    setPushPromptBusy(false);
  };

  const openNotification = async item => {
    if (item.status !== 'read') await markOneRead(item._id);
    setOpen(false);
    // Fall back to the actor's own dashboard (never the public homepage) for
    // legacy/malformed notifications carrying no usable url.
    const url = typeof item.url === 'string' && item.url.startsWith('/') && !item.url.startsWith('//') ? item.url : (dashboardPath || '/');
    window.location.assign(url);
  };

  return (
    <div ref={containerRef} className={`${css.center} ${inline ? css.inline : ''}`}>
      <MainModal isOpen={showPushPrompt} onClose={dismissPushPrompt} width="460px">
        <div className={css.pushPrompt}>
          <NotificationsActiveIcon className={css.pushPromptIcon} />
          <h2>Stay up to date</h2>
          <p>Turn on push notifications for important school updates, approvals, results, and messages.</p>
          {pushPromptError && <p className={css.pushPromptError} role="alert">{pushPromptError}</p>}
          <div className={css.pushPromptActions}>
            <button type="button" className={css.pushPromptSecondary} onClick={dismissPushPrompt} disabled={pushPromptBusy}>Maybe later</button>
            <button type="button" className={css.pushPromptPrimary} onClick={handlePromptEnable} disabled={pushPromptBusy}>
              {pushPromptBusy ? 'Enabling...' : 'Enable notifications'}
            </button>
          </div>
        </div>
      </MainModal>
      <button
        type="button"
        className={css.bellButton}
        onClick={() => { setOpen(value => !value); if (!open) refresh(); }}
        aria-label={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'}
        title="Notifications"
      >
        <NotificationsIcon />
        {unreadCount > 0 && <span className={css.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <section className={`${css.popover} BubblePopIn`} aria-label="School notifications">
          <div className={css.header}>
            <div>
              <h2 className={css.title}>Notifications</h2>
              <p className={css.subtitle}>{unreadCount ? `${unreadCount} unread` : 'All caught up'}</p>
            </div>
            <button type="button" className={css.iconButton} onClick={() => setOpen(false)} aria-label="Close notifications" title="Close">
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className={css.actions}>
            <button type="button" className={css.actionButton} onClick={markAllRead} disabled={!unreadCount}>
              <DoneAllIcon fontSize="small" /> Mark all read
            </button>
            <div className={css.pushControl}>
              <span className={css.pushLabel}>Enable push</span>
              <button
                type="button"
                role="switch"
                aria-checked={pushStatus === 'enabled'}
                aria-label="Enable push notifications"
                className={css.toggleSwitch}
                data-on={pushStatus === 'enabled' ? 'true' : 'false'}
                onClick={() => pushStatus === 'enabled' ? disablePush() : enablePush()}
                disabled={pushBusy || pushStatus === 'unknown'}
              >
                <span className={css.toggleKnob} />
              </button>
            </div>
            <button type="button" className={css.actionButton} onClick={() => setShowPreferences(value => !value)}>
              <SettingsIcon fontSize="small" /> {showPreferences ? 'Hide settings' : 'Settings'}
            </button>
          </div>

          {showPreferences && (
            <div className={css.preferences}>
              <p className={css.preferenceHint}>Optional categories can be muted. Critical account, payment, attendance, and exam alerts remain active.</p>
              {categories.map(([key, label]) => {
                const enabled = preferences.categories?.[key] !== false;
                return (
                  <label key={key} className={css.preferenceRow}>
                    <input
                      className="schoolCheckbox schoolCheckboxNotification"
                      type="checkbox"
                      checked={enabled}
                      onChange={event => updatePreferences({ categories: { ...(preferences.categories || {}), [key]: event.target.checked } })}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          )}

          <div className={css.list}>
            {loading ? <p className={css.empty}>Loading notifications...</p> : items.length === 0 ? <p className={css.empty}>No notifications yet.</p> : items.map(item => (
              <button
                type="button"
                key={String(item._id)}
                className={`${css.item} ${item.status === 'unread' ? css.unread : ''}`}
                onClick={() => openNotification(item)}
              >
                <span className={css.itemDot} aria-hidden="true" />
                <span className={css.itemCopy}>
                  <strong>{item.title || 'Notification'}</strong>
                  {item.description && <span>{item.description}</span>}
                  <small>{formatTime(item.createdAt)}</small>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}